import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';

const superCleanString = (str) => {
    if (!str) return '';
    return str
        .toString()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/đ/g, 'd')
        .replace(/Đ/g, 'D')
        .replace(/\s+/g, ' ')
        .toLowerCase()
        .trim();
};

const SearchableFocusDropdown = ({ value, options, onChange, placeholder, disabled, panelUniqueClass, className, itemTemplate, filterBy }) => {
    const dropdownRef = useRef(null);
    const focusTrapRef = useRef(null);

    const handleShow = () => {
        setTimeout(() => {
            try {
                const panel = document.querySelector(`.${panelUniqueClass}`);
                if (panel) {
                    const filterInput = panel.querySelector('.p-dropdown-filter');
                    if (filterInput && typeof filterInput.focus === 'function') {
                        filterInput.focus();

                        focusTrapRef.current = (e) => {
                            if (e.target !== filterInput) {
                                filterInput.focus();
                            }
                        };

                        panel.addEventListener('focusin', focusTrapRef.current);
                    }
                }
            } catch (err) {
                console.warn("Focus error:", err);
            }
        }, 100);
    };

    const handleHide = () => {
        const panel = document.querySelector(`.${panelUniqueClass}`);
        if (panel && focusTrapRef.current) {
            panel.removeEventListener('focusin', focusTrapRef.current);
        }
    };

    return (
        <Dropdown
            ref={dropdownRef}
            value={value}
            options={options}
            onChange={onChange}
            filter
            filterBy={filterBy || "label"}
            placeholder={placeholder}
            className={className || "custom-dropdown"}
            panelClassName={panelUniqueClass}
            style={{ width: '100%' }}
            appendTo={document.body}
            onShow={handleShow}
            onHide={handleHide}
            disabled={disabled}
            itemTemplate={itemTemplate}
        />
    );
};

const ScienceScoringManagementReferenceForm = ({
    isOpen,
    onClose,
    toast,
    authHeaders,
    data,
    fetchData
}) => {
    const currentYear = useMemo(() => new Date().getFullYear(), []);
    const defaultFromDateStr = useMemo(() => `${currentYear}-01-01`, [currentYear]);
    const defaultToDateStr = useMemo(() => `${currentYear}-12-31`, [currentYear]);

    const initialForm = {
        DeptId: 'All',
        UserId: 'All',
        ScoringContent: 'All',
        ItemStatusFilter: 'All',
        PreviousYearHours: 0,
        Coefficient: 1,
        ActionType: 'View',
        FromDate: defaultFromDateStr,
        ToDate: defaultToDateStr
    };

    const [formData, setFormData] = useState(initialForm);
    const [departments, setDepartments] = useState([]);
    const [users, setUsers] = useState([]);
    const [calcResults, setCalcResults] = useState([]);
    const [isCalculating, setIsCalculating] = useState(false);
    const [hasAutoSaved, setHasAutoSaved] = useState(false);
    const renderHighlightedAuthors = (authorsStr, lecturerName) => {
        if (!lecturerName || !authorsStr) return authorsStr;

        const removeAccents = (str) => str.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/đ/g, 'd').replace(/Đ/g, 'D').toLowerCase();

        const normFullName = removeAccents(lecturerName).trim();
        const nameParts = normFullName.split(/\s+/);
        const lastName = nameParts[0];
        const firstName = nameParts[nameParts.length - 1];

        const chunks = authorsStr.split(/([,;]|\s+and\s+|\s+và\s+)/i);

        return chunks.map((chunk, index) => {
            if (index % 2 !== 0) return <span key={index}>{chunk}</span>;

            const normChunk = removeAccents(chunk).trim();
            const chunkWords = normChunk.replace(/[^a-z0-9\s]/g, ' ').split(/\s+/).filter(Boolean);

            let isMatch = false;
            const hasLastName = chunkWords.includes(lastName);
            const hasFirstName = chunkWords.includes(firstName);
            const hasFirstInitial = chunkWords.some(w => w === firstName[0] && w.length === 1);
            const hasLastInitial = chunkWords.some(w => w === lastName[0] && w.length === 1);

            if (normChunk === normFullName || normChunk.includes(normFullName)) {
                isMatch = true;
            } else if (hasLastName && (hasFirstName || hasFirstInitial)) {
                isMatch = true;
            } else if (hasFirstName && (hasLastName || hasLastInitial)) {
                isMatch = true;
            } else if (normChunk.replace(/\s+/g, '') === nameParts.map(p => p[0]).join('')) {
                isMatch = true;
            }

            if (isMatch) {
                return <strong key={index} style={{ color: '#0056b3', fontWeight: 'bold' }}>{chunk}</strong>;
            }
            return <span key={index}>{chunk}</span>;
        });
    };

    useEffect(() => {
        if (isOpen) {
            loadInitialData();
            setFormData(prev => ({ ...initialForm, ActionType: 'View' }));
            setCalcResults([]);
            setHasAutoSaved(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, defaultFromDateStr, defaultToDateStr]);

    const loadInitialData = async () => {
        try {
            const [deptRes, userRes] = await Promise.all([
                fetch(`${process.env.REACT_APP_API_BASE_URL}/department`, { headers: authHeaders }),
                fetch(`${process.env.REACT_APP_API_BASE_URL}/user`, { headers: authHeaders })
            ]);
            if (deptRes.ok) setDepartments(await deptRes.json());
            if (userRes.ok) setUsers(await userRes.json());
        } catch (e) {
            console.error("Lỗi tải danh mục:", e);
        }
    };

    const deptOptions = useMemo(() => {
        const buildOptions = (parentId = null, level = 0) => {
            let options = [];
            const currentDepts = departments.filter(d =>
                (parentId === null ? (d.ParentId === null || d.ParentId === undefined) : d.ParentId === parentId)
            );

            currentDepts.forEach(d => {
                const prefix = level > 0 ? '\u00A0'.repeat(level * 4) + '˪ ' : '';
                options.push({ label: prefix + d.Name, value: d.Id });
                options = [...options, ...buildOptions(d.Id, level + 1)];
            });
            return options;
        };

        const options = buildOptions();
        return [{ label: "Tất cả Đơn vị", value: "All" }, ...options];
    }, [departments]);

    const getAllDeptIds = (parentId, deptList) => {
        let ids = [parentId];
        const children = deptList.filter(d => d.ParentId === parentId);
        children.forEach(child => {
            ids = [...ids, ...getAllDeptIds(child.Id, deptList)];
        });
        return ids;
    };

    const filteredUsers = useMemo(() => {
        let list = users;
        if (formData.DeptId !== 'All') {
            const validDeptIds = getAllDeptIds(formData.DeptId, departments);
            list = users.filter(u => validDeptIds.includes(u.DepartmentId));
        }
        return list;
    }, [users, formData.DeptId, departments]);

    const userOptions = useMemo(() => {
        const options = filteredUsers.map(u => {
            const dept = departments.find(d => d.Id === u.DepartmentId);
            const fullName = `${u.LastName || ''} ${u.FirstName || ''}`.trim();
            const email = u.Email || '';
            const deptName = dept ? dept.Name : '';
            const rawSearchStr = `${fullName} ${email} ${deptName}`;
            return {
                label: `${fullName} - ${email}`,
                value: u.Id,
                fullName: fullName,
                email: email,
                deptName: deptName,
                searchKey: superCleanString(rawSearchStr),
                isAll: false
            };
        });

        return [
            { label: "Tất cả Giảng viên", value: "All", isAll: true, searchKey: "tat ca giang vien" },
            ...options
        ];
    }, [filteredUsers, departments]);

    const userItemTemplate = (option) => {
        if (option.isAll) {
            return <div style={{ fontWeight: 'bold', padding: '4px 0' }}>{option.label}</div>;
        }
        return (
            <div style={{ display: 'flex', flexDirection: 'column', padding: '4px 0' }}>
                <span style={{ fontWeight: 'bold', color: '#333' }}>{option.fullName}</span>
                <span style={{ fontSize: '12px', color: '#666' }}>
                    {option.email} {option.deptName ? ` - ${option.deptName}` : ''}
                </span>
            </div>
        );
    };

    const handleChange = (e) => {
        const { name, value, type } = e.target;

        setFormData(prev => {
            const newData = {
                ...prev,
                [name]: type === 'number' ? (value === '' ? '' : Number(value)) : value
            };

            if (name === 'DeptId') {
                newData.UserId = 'All';
            }
            return newData;
        });

        setCalcResults([]);
        setHasAutoSaved(false);
    };

    const handleDateChange = (name, dateObj) => {
        let dateString = '';
        if (dateObj) {
            const yyyy = dateObj.getFullYear();
            const mm = String(dateObj.getMonth() + 1).padStart(2, '0');
            const dd = String(dateObj.getDate()).padStart(2, '0');
            dateString = `${yyyy}-${mm}-${dd}`;
        }
        handleChange({ target: { name, value: dateString, type: 'text' } });
    };

    const handleCalculate = async () => {
        let userIdsToCalc = [];
        if (formData.UserId === 'All') {
            userIdsToCalc = filteredUsers.map(u => u.Id);
        } else {
            userIdsToCalc = [formData.UserId];
        }

        if (userIdsToCalc.length === 0) {
            return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Không có giảng viên nào phù hợp để tính điểm!' });
        }

        setIsCalculating(true);
        const results = [];
        let allSavedSuccessfully = true;

        try {
            for (const uid of userIdsToCalc) {
                const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/referencescoringmngt?action=calculatereference`, {
                    method: 'POST',
                    headers: {
                        ...authHeaders,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        UserId: uid,
                        ScoringContent: formData.ScoringContent,
                        ItemStatusFilter: formData.ItemStatusFilter,
                        FromDate: formData.FromDate,
                        ToDate: formData.ToDate,
                        Coefficient: formData.Coefficient
                    })
                });

                if (response.ok) {
                    const resData = await response.json();

                    const totalConverted = parseFloat(resData.Summary.ConvertedHours) || 0;
                    const extHours = parseFloat(formData.PreviousYearHours) || 0;
                    const reqHours = resData.Summary.RequiredHours != null ? parseFloat(resData.Summary.RequiredHours) : 600;
                    resData.Summary.PreviousYearHours = extHours;
                    resData.Summary.DifferenceHours = (totalConverted + extHours) - reqHours;

                    const user = users.find(u => u.Id === uid);
                    const dept = departments.find(d => d.Id === user?.DepartmentId);

                    resData.Summary.LecturerName = `${user?.LastName || ''} ${user?.FirstName || ''}`.trim();
                    resData.Summary.DepartmentName = dept?.Name || "Chưa xếp đơn vị";
                    resData.Summary.UserId = uid;

                    results.push(resData);

                    if (formData.ActionType === 'Save') {
                        let finalId = 0;
                        if (data) {
                            const existingItem = data.find(d => d.UserId === uid && d.FromDate?.split('T')[0] === formData.FromDate && d.ToDate?.split('T')[0] === formData.ToDate && d.Status === 'Tham khảo');
                            if (existingItem && existingItem.Status !== 'Đã duyệt') {
                                finalId = existingItem.Id;
                            }
                        }

                        const finalData = {
                            ...resData.Summary,
                            Id: finalId,
                            UserId: uid,
                            ScoringContent: formData.ScoringContent,
                            ItemStatusFilter: formData.ItemStatusFilter,
                            FromDate: formData.FromDate,
                            ToDate: formData.ToDate,
                            ExternalHours: formData.PreviousYearHours
                        };

                        const saveResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/referencescoringmngt`, {
                            method: finalId ? 'PUT' : 'POST',
                            headers: {
                                ...authHeaders,
                                'Content-Type': 'application/json'
                            },
                            body: JSON.stringify(finalData)
                        });

                        if (!saveResponse.ok) {
                            allSavedSuccessfully = false;
                        }
                    }
                }
            }

            setCalcResults(results);

            if (formData.ActionType === 'Save') {
                if (allSavedSuccessfully && results.length > 0) {
                    toast.current?.show({ severity: 'success', summary: 'Thành công', detail: `Đã tự động tính và lưu kết quả (Tham khảo) cho ${results.length} giảng viên!` });
                    setHasAutoSaved(true);
                    if (fetchData) fetchData(true);
                } else {
                    toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Tính điểm xong nhưng có lỗi khi lưu tự động cho một số giảng viên.' });
                }
            } else {
                toast.current?.show({ severity: 'info', summary: 'Thành công', detail: `Đã tải kết quả dự kiến cho ${results.length} giảng viên!` });
            }

        } catch (error) {
            toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Hệ thống gặp sự cố trong quá trình kết nối API tham khảo.' });
        } finally {
            setIsCalculating(false);
        }
    };

    const handleSave = async () => {
        if (calcResults.length === 0) return;
        let allSuccess = true;

        try {
            for (const res of calcResults) {
                let finalId = 0;
                if (data) {
                    const existingItem = data.find(d => d.UserId === res.Summary.UserId && d.FromDate?.split('T')[0] === formData.FromDate && d.ToDate?.split('T')[0] === formData.ToDate && d.Status === 'Tham khảo');
                    if (existingItem && existingItem.Status !== 'Đã duyệt') {
                        finalId = existingItem.Id;
                    }
                }

                const finalData = {
                    ...res.Summary,
                    Id: finalId,
                    UserId: res.Summary.UserId,
                    ScoringContent: formData.ScoringContent,
                    ItemStatusFilter: formData.ItemStatusFilter,
                    FromDate: formData.FromDate,
                    ToDate: formData.ToDate,
                    ExternalHours: formData.PreviousYearHours
                };

                const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/referencescoringmngt`, {
                    method: finalId ? 'PUT' : 'POST',
                    headers: {
                        ...authHeaders,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(finalData)
                });

                if (!response.ok) {
                    allSuccess = false;
                }
            }

            if (allSuccess) {
                toast.current?.show({ severity: 'success', summary: 'Thành công', detail: 'Đã lưu kết quả (Tham khảo) cho toàn bộ giảng viên!' });
                if (fetchData) fetchData(true);
                onClose();
            } else {
                toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Có lỗi xảy ra khi lưu kết quả một số giảng viên.' });
            }
        } catch (error) {
            toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Không thể kết nối máy chủ để lưu kết quả.' });
        }
    };

    if (!isOpen) return null;

    return (
        <div className="modal-overlay" style={{ zIndex: 1000 }}>
            <div className="modal-box" style={{ width: '95%', maxWidth: '1200px', maxHeight: '90vh', overflowY: 'auto', padding: 0 }}>
                <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <h3 style={{ margin: 0, paddingRight: '20px', lineHeight: '1.4', color: '#e67e22' }}>
                        <i className="fa-solid fa-calculator" style={{ marginRight: '8px' }}></i> Tính điểm khoa học (Tham khảo)
                    </h3>
                    <button className="close-btn" onClick={onClose} style={{ fontSize: '26px', lineHeight: '1', flexShrink: 0, marginTop: '-2px' }}>
                        &times;
                    </button>
                </div>

                <div className="modal-body" style={{ padding: '25px' }}>
                    <form id="scienceScoringReferenceForm">
                        <div className="form-grid-2" style={{ marginBottom: '20px' }}>
                            <div className="form-group">
                                <label>Chọn đơn vị *</label>
                                <Dropdown
                                    name="DeptId"
                                    value={formData.DeptId}
                                    options={deptOptions}
                                    onChange={(e) => handleChange({ target: { name: 'DeptId', value: e.value } })}
                                    filter
                                    placeholder="Chọn đơn vị"
                                    style={{ width: '100%' }}
                                    className="custom-dropdown"
                                />
                            </div>

                            <div className="form-group">
                                <label>Chọn giảng viên *</label>
                                <SearchableFocusDropdown
                                    value={formData.UserId}
                                    options={userOptions}
                                    onChange={(e) => handleChange({ target: { name: 'UserId', value: e.value } })}
                                    placeholder="Chọn giảng viên"
                                    panelUniqueClass="custom-dropdown-panel-scoring-user"
                                    itemTemplate={userItemTemplate}
                                    filterBy="label,email,deptName,searchKey"
                                />
                            </div>
                        </div>

                        <div className="form-grid-2" style={{ marginBottom: '20px' }}>
                            <div className="form-group">
                                <label>Nội dung tính điểm *</label>
                                <select
                                    name="ScoringContent"
                                    className="form-input"
                                    value={formData.ScoringContent}
                                    onChange={handleChange}
                                    required
                                >
                                    <option value="All">Tất cả công trình</option>
                                    <option value="Article">Bài báo</option>
                                    <option value="Project">Đề tài</option>
                                    <option value="Book">Sách - Giáo trình</option>
                                    <option value="Other">Nội dung NCKH Khác</option>
                                </select>
                            </div>

                            <div className="form-group">
                                <label>Trạng thái (Tham khảo)*</label>
                                <select
                                    name="ItemStatusFilter"
                                    className="form-input"
                                    value={formData.ItemStatusFilter}
                                    onChange={handleChange}
                                    required
                                >
                                    <option value="All">Tất cả trạng thái (Bao gồm Nháp)</option>
                                    <option value="Approved">Chỉ lấy Đã duyệt</option>
                                    <option value="Pending">Đã gửi duyệt</option>
                                    <option value="Draft">Đang chỉnh sửa</option>
                                </select>
                            </div>

                            <div className="form-group">
                                <label>Số giờ năm ngoái chuyển qua</label>
                                <input
                                    type="number"
                                    name="PreviousYearHours"
                                    className="form-input"
                                    value={formData.PreviousYearHours}
                                    onChange={handleChange}
                                />
                            </div>

                            <div className="form-group">
                                <label>Hành động *</label>
                                <select
                                    name="ActionType"
                                    className="form-input"
                                    value={formData.ActionType}
                                    onChange={handleChange}
                                    required
                                >
                                    <option value="View">Chỉ xem bảng điểm tham khảo</option>
                                    <option value="Save">Xem và lưu tự động với status Tham khảo</option>
                                </select>
                            </div>

                            <div className="form-group">
                                <label>Hệ số phạt/thưởng (Base) *</label>
                                <input
                                    type="number"
                                    step="0.1"
                                    name="Coefficient"
                                    className="form-input"
                                    value={formData.Coefficient}
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                        </div>

                        <div className="form-grid-2" style={{ marginBottom: '20px' }}>
                            <div className="form-group">
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Từ ngày (Khoảng xét duyệt)*</label>
                                <Calendar
                                    id="FromDate"
                                    name="FromDate"
                                    value={(formData.FromDate && !isNaN(new Date(formData.FromDate).getTime())) ? new Date(formData.FromDate) : null}
                                    dateFormat="dd/mm/yy"
                                    showIcon
                                    onChange={(e) => handleDateChange('FromDate', e.value)}
                                    style={{ width: '100%' }}
                                    appendTo={document.body}
                                    panelStyle={{ marginTop: '5px', boxShadow: '0 4px 15px rgba(0,0,0,0.1)', borderRadius: '8px', zIndex: 99999 }}
                                    inputStyle={{ padding: '10px 12px', border: '1px solid #cbd5e1', borderRight: 'none', borderRadius: '6px 0 0 6px', fontSize: '14px', outline: 'none' }}
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Đến ngày (Khoảng xét duyệt)*</label>
                                <Calendar
                                    id="ToDate"
                                    name="ToDate"
                                    value={(formData.ToDate && !isNaN(new Date(formData.ToDate).getTime())) ? new Date(formData.ToDate) : null}
                                    dateFormat="dd/mm/yy"
                                    showIcon
                                    onChange={(e) => handleDateChange('ToDate', e.value)}
                                    style={{ width: '100%' }}
                                    appendTo={document.body}
                                    panelStyle={{ marginTop: '5px', boxShadow: '0 4px 15px rgba(0,0,0,0.1)', borderRadius: '8px', zIndex: 99999 }}
                                    inputStyle={{ padding: '10px 12px', border: '1px solid #cbd5e1', borderRight: 'none', borderRadius: '6px 0 0 6px', fontSize: '14px', outline: 'none' }}
                                    required
                                />
                            </div>
                        </div>
                    </form>

                    {calcResults.length > 0 && (
                        <div style={{ borderTop: '1px solid #eee', paddingTop: '20px', marginTop: '10px' }}>

                            <h4 style={{ color: '#e67e22', marginBottom: '15px', textAlign: 'center' }}>KẾT QUẢ TÍNH ĐIỂM DỰ KIẾN CHI TIẾT</h4>

                            {calcResults.map((res, index) => (
                                <div key={index} style={{ marginBottom: '40px', border: '1px solid #ddd', padding: '15px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                                    <div style={{ backgroundColor: '#f8f9fa', padding: '12px', borderRadius: '5px', marginBottom: '15px', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', borderLeft: '4px solid #f39c12' }}>
                                        <span style={{ fontWeight: 'bold', fontSize: '15px' }}>
                                            Giảng viên: <span style={{ color: '#e74c3c' }}>{res.Summary.LecturerName}</span>
                                        </span>
                                        <span style={{ fontWeight: 'bold', fontSize: '15px' }}>
                                            Đơn vị: <span style={{ color: '#2980b9' }}>{res.Summary.DepartmentName}</span>
                                        </span>
                                    </div>

                                    {res.Summary.AppliedConfigVersionName && (
                                        <div style={{ backgroundColor: '#fff3cd', padding: '10px 15px', borderRadius: '5px', marginBottom: '15px', borderLeft: '4px solid #f39c12', fontSize: '14px', color: '#856404' }}>
                                            <i className="fa-solid fa-lightbulb" style={{ marginRight: '8px' }}></i>
                                            <strong>Cơ chế định mức chung:</strong> {res.Summary.AppliedConfigVersionName}
                                        </div>
                                    )}

                                    <h5 style={{ color: '#555', marginBottom: '10px', marginTop: '0' }}>1. TỔNG HỢP SỐ GIỜ NCKH DỰ KIẾN</h5>
                                    <div style={{ overflowX: 'auto', marginBottom: '20px' }}>
                                        <table className="custom-table" style={{ minWidth: '800px', margin: '0' }}>
                                            <thead>
                                                <tr>
                                                    <th>Chức danh</th>
                                                    <th>Định mức (giờ)</th>
                                                    <th>Tỉ lệ giảm (%)</th>
                                                    <th>Cần đạt (giờ)</th>
                                                    <th>Tổng điểm</th>
                                                    <th>Hệ số</th>
                                                    <th>Năm trước (giờ)</th>
                                                    <th>Giờ quy đổi (giờ)</th>
                                                    <th>Chênh lệch dự kiến</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <td>{res.Summary.JobTitle}</td>
                                                    <td style={{ textAlign: 'center' }}>{res.Summary.StandardHours}</td>
                                                    <td style={{ textAlign: 'center' }}>
                                                        {Math.round(Number(res.Summary.ReductionPercentage) * 100) / 100}
                                                    </td>
                                                    <td style={{ textAlign: 'center', fontWeight: 'bold', color: '#e67e22' }}>
                                                        {Number(parseFloat(res.Summary.RequiredHours || 0).toFixed(2))}
                                                    </td>
                                                    <td style={{ textAlign: 'center', fontWeight: 'bold', color: '#3498db' }}>
                                                        {Number(parseFloat(res.Summary.AchievedScore || 0).toFixed(2))}
                                                    </td>
                                                    <td style={{ textAlign: 'center' }}>{res.Summary.Coefficient}</td>
                                                    <td style={{ textAlign: 'center', color: '#28a745' }}>{res.Summary.PreviousYearHours}</td>
                                                    <td style={{ textAlign: 'center', fontWeight: 'bold' }}>
                                                        {Number(parseFloat(res.Summary.ConvertedHours || 0).toFixed(2))}
                                                    </td>
                                                    <td style={{ textAlign: 'center', fontWeight: 'bold', color: res.Summary.DifferenceHours >= 0 ? '#28a745' : '#dc3545' }}>
                                                        {Number(parseFloat(res.Summary.DifferenceHours || 0).toFixed(2))}
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>

                                    <h5 style={{ color: '#555', marginBottom: '10px' }}>2. BẢNG LIỆT KÊ & ÁP DỤNG CƠ CHẾ CHI TIẾT TỪNG CÔNG TRÌNH</h5>
                                    <div style={{ overflowX: 'auto' }}>
                                        <table className="custom-table" style={{ minWidth: '1100px', margin: '0' }}>
                                            <thead>
                                                <tr>
                                                    <th width="40px" style={{ textAlign: 'center' }}>STT</th>
                                                    <th width="90px" style={{ textAlign: 'left' }}>Loại</th>
                                                    <th style={{ textAlign: 'left' }}>Tiêu đề</th>
                                                    <th width="150px" style={{ textAlign: 'left' }}>Nơi đăng/Cấp</th>
                                                    <th width="80px" style={{ textAlign: 'center' }}>Điểm tạp chí</th>
                                                    <th width="90px" style={{ textAlign: 'center' }}>Ngày NT/XB</th>
                                                    <th width="150px" style={{ textAlign: 'left' }}>Tác giả</th>
                                                    <th width="110px" style={{ textAlign: 'left' }}>Vai trò</th>
                                                    <th width="110px" style={{ textAlign: 'center' }}>Cơ chế áp dụng</th>
                                                    <th width="70px" style={{ textAlign: 'center' }}>Số điểm</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {res.Details && res.Details.length > 0 ? (
                                                    res.Details.map((d, idx) => (
                                                        <tr key={idx}>
                                                            <td style={{ textAlign: 'center' }}>{idx + 1}</td>
                                                            <td style={{ fontWeight: 'bold', color: '#666', fontSize: '13px', whiteSpace: 'nowrap', textAlign: 'left' }}>{d.Type}</td>
                                                            <td style={{ width: '25%', textAlign: 'left' }}>{d.Title}</td>
                                                            <td style={{ textAlign: 'left' }}>{d.Journal}</td>
                                                            <td style={{ textAlign: 'center', fontWeight: 'bold', color: '#e67e22' }}>{d.BaseScore ?? '-'}</td>
                                                            <td style={{ textAlign: 'center' }}>{d.PublishDate}</td>
                                                            <td style={{ fontSize: '13px', textAlign: 'left', lineHeight: '1.4' }}>
                                                                {renderHighlightedAuthors(d.Authors, res.Summary.LecturerName)}
                                                            </td>
                                                            <td style={{ textAlign: 'left', fontSize: '13px', whiteSpace: 'nowrap' }}>
                                                                <span style={{ backgroundColor: '#f1f5f9', padding: '4px 8px', borderRadius: '4px', border: '1px solid #cbd5e1', display: 'inline-block', whiteSpace: 'nowrap' }}>
                                                                    {d.Role || '-'}
                                                                </span>
                                                            </td>
                                                            <td style={{ textAlign: 'center' }}>
                                                                <span style={{ fontSize: '11px', color: '#16a085', fontWeight: '600', backgroundColor: '#e8f8f5', padding: '4px 8px', borderRadius: '4px', border: '1px solid #d7bde2', display: 'inline-block' }}>
                                                                    {d.VersionName || 'Mặc định'}
                                                                </span>
                                                            </td>
                                                            <td style={{ textAlign: 'center', fontWeight: 'bold', color: '#0056b3', fontSize: '15px' }}>
                                                                {Number(d.Score).toFixed(2)}
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan="10" style={{ textAlign: 'center', padding: '20px', color: '#888' }}>
                                                            Không có công trình nào trong khoảng thời gian này
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="modal-overlay-footer" style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', padding: '15px 25px', borderTop: '1px solid #eee' }}>
                    <button type="button" className="btn-cancel" onClick={onClose}>
                        {hasAutoSaved ? 'Đóng' : 'Hủy'}
                    </button>
                    <button
                        type="button"
                        className="btn-submit"
                        style={{
                            backgroundColor: '#f39c12',
                            opacity: (!formData.FromDate || !formData.ToDate) ? 0.6 : 1,
                            cursor: (!formData.FromDate || !formData.ToDate) ? 'not-allowed' : 'pointer'
                        }}
                        onClick={handleCalculate}
                        disabled={!formData.FromDate || !formData.ToDate || isCalculating}
                    >
                        {isCalculating ? (
                            <><i className="fa-solid fa-circle-notch fa-spin" style={{ marginRight: '6px' }}></i>Đang tính</>
                        ) : 'Tính điểm tham khảo'}
                    </button>
                    {calcResults.length > 0 && !hasAutoSaved && formData.ActionType === 'View' && (
                        <button type="button" className="btn-submit" style={{ backgroundColor: '#28a745' }} onClick={handleSave}>
                            Lưu kết quả
                        </button>
                    )}
                </div>

            </div>
        </div>
    );
};

export default ScienceScoringManagementReferenceForm;